import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  createHttpLink,
} from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { split } from '@apollo/client/link';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ApolloProvider as BaseApolloProvider } from '@apollo/client/react';
import { getMainDefinition } from '@apollo/client/utilities';
import { LocalStorageWrapper, persistCacheSync } from 'apollo3-cache-persist';
import { createClient } from 'graphql-ws';
import { ReactNode, useMemo } from 'react';
import { Observable } from 'rxjs';

import { REFRESH_ACCESS_TOKEN } from '#graphql/mutations/login';
import { getApolloPersistStorageKey } from '#utils/apollo-persist-key';
import {
  isAuthSessionRefreshDead,
  markAuthSessionRefreshDead,
  resetAuthSessionRefreshCircuit,
} from '#utils/auth-refresh-circuit';

import { useAppConfig } from './app-config-provider';

function graphqlErrorLooksUnauthenticated(e: {
  extensions?: Record<string, unknown>;
  message?: string;
}): boolean {
  const code = (e.extensions?.code as string) ?? '';
  if (code === 'UNAUTHENTICATED') return true;

  const msg = e.message?.toLowerCase() ?? '';
  return (
    msg.includes('unauthorized') ||
    msg.includes('authentication required') ||
    msg.includes('invalid or expired token') ||
    msg.includes('refresh token required') ||
    msg.includes('refresh token') ||
    msg.includes('user not found')
  );
}

function is401(error: import('@apollo/client').ErrorLike): boolean {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const statusCode = (error as { statusCode?: number }).statusCode;
    if (statusCode === 401) return true;
  }

  if (CombinedGraphQLErrors.is(error)) {
    return error.errors.some((e) => graphqlErrorLooksUnauthenticated(e));
  }

  return error?.message?.toLowerCase().includes('unauthorized') ?? false;
}

function terminalAuthErrorObservable(
  error: import('@apollo/client').ErrorLike,
): Observable<import('@apollo/client').FetchResult> {
  return new Observable((subscriber) => {
    subscriber.error(error);
  });
}

/** Same path as HTTP GraphQL; `wss` when the page is HTTPS. */
function graphqlWebSocketUrl(httpGraphqlUrl: string): string {
  if (typeof window === 'undefined') {
    return 'ws://127.0.0.1:3001/api/graphql';
  }

  const u = new URL(httpGraphqlUrl, window.location.href);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  return u.toString();
}

function createApolloClient(graphqlUrl: string) {
  resetAuthSessionRefreshCircuit();
  const clientRef: { current: ApolloClient | null } = { current: null };

  let refreshPromise: Promise<unknown> | null = null;

  const errorLink = onError(({ error, operation, forward }) => {
    if (!is401(error)) {
      return forward(operation);
    }

    if (isAuthSessionRefreshDead()) {
      return terminalAuthErrorObservable(error);
    }

    const context = operation.getContext?.() ?? {};
    // Allow callers to explicitly opt out of auth-refresh behavior for a given
    // operation (e.g. initial Me check, refresh mutation itself, or public
    // queries where 401s are expected and should not trigger refresh).
    if (context.skipAuthRefresh) {
      return forward(operation);
    }

    // Check if this is a refresh token error - if so, immediately mark as dead
    const errorMsg = error?.message?.toLowerCase() ?? '';
    if (CombinedGraphQLErrors.is(error)) {
      // Check GraphQL errors for refresh token messages
      const hasRefreshTokenError = error.errors.some((e) => {
        const msg = e.message?.toLowerCase() ?? '';
        return (
          msg.includes('refresh token required') ||
          msg.includes('refresh token')
        );
      });
      if (hasRefreshTokenError) {
        markAuthSessionRefreshDead();
        if (typeof window !== 'undefined') {
          try {
            window.localStorage.removeItem(
              getApolloPersistStorageKey(graphqlUrl),
            );
          } catch {
            // ignore storage errors
          }
        }

        return terminalAuthErrorObservable(error);
      }
    } else if (
      errorMsg.includes('refresh token required') ||
      errorMsg.includes('refresh token')
    ) {
      markAuthSessionRefreshDead();
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.removeItem(
            getApolloPersistStorageKey(graphqlUrl),
          );
        } catch {
          // ignore storage errors
        }
      }

      return terminalAuthErrorObservable(error);
    }

    const client = clientRef.current;
    if (!client) {
      return forward(operation);
    }

    if (!refreshPromise) {
      refreshPromise = client
        .mutate({
          mutation: REFRESH_ACCESS_TOKEN,
          context: { skipAuthRefresh: true },
        })
        .then(() => {
          resetAuthSessionRefreshCircuit();
          refreshPromise = null;
        })
        .catch((err: unknown) => {
          markAuthSessionRefreshDead();
          refreshPromise = null;
          if (typeof window !== 'undefined') {
            try {
              window.localStorage.removeItem(
                getApolloPersistStorageKey(graphqlUrl),
              );
            } catch {
              // ignore storage errors
            }
          }

          throw err;
        });
    }

    const currentRefresh = refreshPromise;
    return new Observable((subscriber) => {
      currentRefresh
        .then(() => {
          const obs = forward(operation);
          obs.subscribe(subscriber);
        })
        .catch((err: unknown) => {
          void client.resetStore();
          subscriber.error(err);
        });
    });
  });

  const httpLink = createHttpLink({
    uri: graphqlUrl,
    credentials: 'include',
  });

  const wsLink =
    typeof window !== 'undefined'
      ? new GraphQLWsLink(
          createClient({
            url: graphqlWebSocketUrl(graphqlUrl),
            retryAttempts: 8,
          }),
        )
      : null;

  const httpOrSplitLink =
    wsLink != null
      ? split(
          ({ query }) => {
            const def = getMainDefinition(query);
            return (
              def.kind === 'OperationDefinition' &&
              def.operation === 'subscription'
            );
          },
          wsLink,
          httpLink,
        )
      : httpLink;

  const cache = new InMemoryCache();

  if (typeof window !== 'undefined') {
    try {
      persistCacheSync({
        cache,
        storage: new LocalStorageWrapper(window.localStorage),
        key: getApolloPersistStorageKey(graphqlUrl),
        debounce: 500,
      });
    } catch {
      // Corrupt or incompatible persisted payload — ignore; fresh cache is fine.
    }
  }

  const client = new ApolloClient({
    link: ApolloLink.from([errorLink, httpOrSplitLink]),
    cache,
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'cache-and-network',
      },
    },
  });

  clientRef.current = client;
  return client;
}

interface ApolloProviderProps {
  children: ReactNode;
}

export function ApolloProvider({ children }: ApolloProviderProps) {
  const { config } = useAppConfig();
  const client = useMemo(
    () => createApolloClient(config.graphqlUrl),
    [config.graphqlUrl],
  );

  return <BaseApolloProvider client={client}>{children}</BaseApolloProvider>;
}
